import { useEffect, useRef, useState } from 'react';

import { fetchPoiHistory } from '@/api/poiHistoryApi';
import type { PoiHistoryResponse } from '@/api/poiHistoryApi';
import { showErrorToast } from '@/errors/showErrorToast';

export interface PoiHistoryState {
    history: PoiHistoryResponse | null;
    isLoading: boolean;
    isError: boolean;
}

const IDLE: PoiHistoryState = { history: null, isLoading: false, isError: false };

/**
 * Fetches the history timeline for the selected POI on demand.
 *
 * Nothing is preloaded: `enabled` stays false until the user first opens the History tab
 * for the current POI, so Details-only browsing (Previous/Next across a worklist) never
 * fires history requests. Results are memoised in a ref-held `Map` keyed by
 * client + POI, so re-opening the tab or returning from an event detail is served from
 * cache with no refetch and no loading flicker. The in-flight request is aborted when
 * the selection changes, so a stale response can never overwrite a newer one.
 *
 * `retry` bypasses the cache for the current POI and refetches — surfaced as the
 * "Try Again" action in the error state.
 */
export function usePoiHistory(
    poiId: string | null,
    clientId: string | null,
    enabled: boolean,
): PoiHistoryState & { retry: () => void } {
    const [state, setState] = useState<PoiHistoryState>(IDLE);
    const [attempt, setAttempt] = useState(0);
    const cacheRef = useRef<Map<string, PoiHistoryResponse>>(new Map());

    useEffect(() => {
        if (!enabled || poiId == null || !clientId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
            setState(IDLE);
            return;
        }

        const cacheKey = `${clientId}:${poiId}`;
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            setState({ history: cached, isLoading: false, isError: false });
            return;
        }

        const controller = new AbortController();
        // eslint-disable-next-line react-x/set-state-in-effect
        setState({ history: null, isLoading: true, isError: false });

        fetchPoiHistory(clientId, poiId, controller.signal)
            .then((data) => {
                cacheRef.current.set(cacheKey, data);
                setState({ history: data, isLoading: false, isError: false });
            })
            .catch((error: unknown) => {
                // The selection moved on (or unmounted) — discard this stale result silently.
                if (controller.signal.aborted) return;
                showErrorToast(error);
                setState({ history: null, isLoading: false, isError: true });
            });

        return () => {
            controller.abort();
        };
    }, [enabled, poiId, clientId, attempt]);

    // Evicting the cache entry (a no-op after a failure, which caches nothing) and bumping
    // `attempt` re-runs the effect for the CURRENT poi only — other POIs' cached histories
    // are untouched, and a later switch back to them still serves from cache.
    const retry = () => {
        if (poiId != null && clientId) cacheRef.current.delete(`${clientId}:${poiId}`);
        setAttempt((n) => n + 1);
    };

    return { ...state, retry };
}
